<script setup lang="ts">
import { computed, watch } from 'vue'
import { useIdeStore } from '@/stores/ide.store'
import advanceCompilerDatabaseService from '@/services/ide/advanceCompilerDatabase.service'
import { useAdvanceCompilerDatabaseStore } from '@/stores/advanceCompilerDatabase.store'
import DataBaseTreeItemComp from '@/components/ide/ide/DataBaseTreeItemComp.vue'
import reload from '@/assets/images/ide/reload.svg'
import databaseIcon from '@/assets/images/ide/database.svg'
import minimizIcon from '@/assets/images/ide/arrows-move-horizontal.svg'

const ideStore = useIdeStore()
const advCompDbStore = useAdvanceCompilerDatabaseStore()

const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})

const databaseInstanceName = computed(() => {
  return advCompDbStore.databaseInstanceName
})

const isPodInitializedCompleted = computed(() => {
  return advCompDbStore.isPodInitializationCompleted
})

/**
 * @description Collapses the project tree
 */
const collapseProjectTree = async () => {
  ideStore.setProjectTreeCollapsed(true)
}
/**
 * Asynchronously fetches the databases information for the current project language
 * by sending a request to the `advanceCompilerDatabaseService`.
 */
const fetchDataBases = async () => {
  const projectLanguage = ideStore.isLanguage as string
  const request = {
    language: `${projectLanguage}`
  }
  if (isPodInitializedCompleted.value) {
    const result = await advanceCompilerDatabaseService.listAllDatabases(request)

    const databases = advanceCompilerDatabaseService.formatDatabaseArray(result)

    if (databases.length > 0) {
      advCompDbStore.setDatabaseListNames(databases)
    } else if (databases.length == 0) {
      advCompDbStore.setDatabaseListNames([])
      advCompDbStore.setDatabaseListFetchCompleted(true)
    } else {
      advCompDbStore.setDatabaseListFetchCompleted(false)
    }
  }
}
/**
 * Reloads the list of all databases from the advanced compiler database service.
 */
const reloadDatabaseList = async () => {
  const language = ideStore.isLanguage
  await advanceCompilerDatabaseService.listAllDatabases({ language })
}

watch(
  () => isPodInitializedCompleted.value,
  (value) => {
    if (value) {
      fetchDataBases()
    }
  }
)
</script>

<template>
  <div
    :class="[
      'stroke-primary-default',
      'border-r',
      'h-full',
      'w-full',
      'relative',

      'flex flex-col',

      {
        'lg:h-full': isFullScreen
      },
      {
        'h-full': !isFullScreen
      }
    ]"
  >
    <div
      class="stroke-primary-default flex w-full items-center justify-between border-b px-1.5 py-2.5"
    >
      <div class="flex w-fit items-center gap-3">
        <button @click="reloadDatabaseList" class="focus:outline-none">
          <img :src="reload" class="h-5 w-5" />
        </button>
      </div>

      <button @click="collapseProjectTree">
        <div class="h-5 w-5">
          <img :src="minimizIcon" class="icon-ide-emphasized" />
        </div>
      </button>
    </div>
    <div
      class="background-primary mx-0 flex h-full w-full flex-col overflow-auto"
      style="scrollbar-gutter: stable"
    >
      <div class="h-full w-full">
        <div v-if="databaseInstanceName" class="flex flex-row gap-2 py-2 pl-2">
          <img :src="databaseIcon" class="mt-0.5 h-4 w-4" />
          <div class="text-high flex items-center break-all text-[13px] font-medium leading-snug">
            {{ databaseInstanceName }}
          </div>
        </div>
        <div v-else class="flex w-full flex-col justify-between gap-2 p-4 py-8">
          <div class="text-high flex justify-center text-xs font-medium leading-tight">
            Loading Info...
          </div>
        </div>
        <div v-if="databaseInstanceName" class="flex w-full justify-between gap-2 pl-4">
          <DataBaseTreeItemComp />
        </div>
      </div>
    </div>
  </div>
</template>
